import type { IMediaURLResolverParameters, MediaBinaryStorageTypeMap } from '../interface/cache';
import cacheUtil from './cache.util';

export type CacheResolver = () => Promise<Cache>;

/* to save on effort on repeated slow-downs opening cache, save the output & re-use each time it is requested */
const cachedCacheResolver = (cacheName: string): CacheResolver => {
    let cacheHandle: Cache | null = null;

    const resolver = async () => {
        if (!cacheHandle) {
            cacheHandle = await cacheUtil.openCache(cacheName);
        }
        return cacheHandle;
    };

    return resolver;
};

function _isCachedItem(
    cacheResolver: CacheResolver,
    mediaId: string,
    urlResolver: MediumPathResolver,
): Promise<boolean> {
    return new Promise(async function (resolve, reject) {
        try {
            const cache = await cacheResolver();

            const allRequests = await cacheUtil.mediaRequestList(mediaId, urlResolver);

            /* early exit when we get a hit */
            for (const request of allRequests) {
                const responses = await cacheUtil.cacheResponsesForRequests([request], cache);

                if (responses.length > 0) {
                    resolve(true);
                    return;
                }
            }

            resolve(false);
        } catch (error) {
            reject(error);
        }
    });
}

function _isCached(
    cacheResolver: CacheResolver,
    mediaIdArg: string | Array<string>,
    urlResolver: MediumPathResolver,
): Promise<boolean> {
    return new Promise(async function (resolve, reject) {
        try {
            const mediaIds = Array.isArray(mediaIdArg) ? mediaIdArg : [mediaIdArg];

            const cacheResponses = await Promise.all(
                mediaIds.map(mediaId => _isCachedItem(cacheResolver, mediaId, urlResolver)),
            );

            const missingCacheValues = cacheResponses.filter(isCached => !isCached).length > 0;

            const isAllCached = !missingCacheValues;

            resolve(isAllCached);
        } catch (error) {
            reject(error);
        }
    });
}

function _removeFromCache(
    cacheResolver: CacheResolver,
    mediaIdIArg: string | Array<string>,
    urlResolver: MediumPathResolver,
): Promise<void> {
    return new Promise(async function (resolve, reject) {
        try {
            const cache = await cacheResolver();

            const mediaIds = Array.isArray(mediaIdIArg) ? mediaIdIArg : [mediaIdIArg];

            await cacheUtil.deleteMediaIdsFromCache(cache, mediaIds, urlResolver);

            resolve();
        } catch (error) {
            reject(error);
        }
    });
}

async function _addToCache(
    cacheResolver: CacheResolver,
    request: RequestInfo,
    response: Response,
): Promise<void> {
    const cache = await cacheResolver();

    await cache.put(request, response);
}

function _cachedResponse<
    T extends keyof MediaBinaryStorageTypeMap,
    U extends MediaBinaryStorageTypeMap[T],
>(
    cacheResolver: CacheResolver,
    mediaId: string,
    urlResolver: MediumPathResolver,
    type: T,
): Promise<U> {
    return new Promise(async function (resolve, reject) {
        try {
            const cache = await cacheResolver();

            const allRequests = await cacheUtil.mediaRequestList(mediaId, urlResolver);

            for (const request of allRequests) {
                const responseList = await cacheUtil.cacheResponsesForRequests([request], cache);

                const response = responseList.pop();

                if (!response) {
                    continue;
                }

                switch (type) {
                    case 'Blob':
                        resolve((await response.blob()) as U);
                        return;

                    case 'ArrayBuffer':
                        resolve((await response.arrayBuffer()) as U);
                        return;

                    case 'none':
                        resolve(undefined as U);
                        return;

                    default:
                        throw new Error(`Unknown type for cache search: ${type}`);
                }
            }

            reject(new Error(`Unable to find id: ${mediaId} in cache`));
        } catch (error) {
            reject(error);
        }
    });
}

export interface IMediaCacheSource {
    cacheName: () => string;
    isCached: (mediaId: string | string[]) => Promise<boolean>;
    removeFromCache: (mediaId: string) => Promise<void>;
    addToCache: (request: RequestInfo, response: Response) => Promise<void>;
    cachedResponse<
        T extends keyof MediaBinaryStorageTypeMap,
        U extends MediaBinaryStorageTypeMap[T],
    >(
        mediaId: string,
        type: T,
    ): Promise<U>;
}

export type MediumPathResolver = (
    mediaId: string,
    parameters?: IMediaURLResolverParameters,
) => Array<string>;

function cacheSource(cacheName: string, mediaIdResolver: MediumPathResolver): IMediaCacheSource {
    const cacheResolver = cachedCacheResolver(cacheName);

    const controller: IMediaCacheSource = {
        cacheName: () => cacheName,
        isCached: (mediaId: string | string[]) =>
            _isCached(cacheResolver, mediaId, mediaIdResolver),
        removeFromCache: (mediaId: string) =>
            _removeFromCache(cacheResolver, mediaId, mediaIdResolver),
        addToCache: (request: RequestInfo, response: Response) =>
            _addToCache(cacheResolver, request, response),
        cachedResponse: <
            T extends keyof MediaBinaryStorageTypeMap,
            U extends MediaBinaryStorageTypeMap[T],
        >(
            mediaId: string,
            type: T,
        ) => _cachedResponse(cacheResolver, mediaId, mediaIdResolver, type) as Promise<U>,
    };

    return controller;
}

export default cacheSource;
